import React, { useEffect, useState, Children } from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Lock, Check, ChevronLeft, ChevronDown, ChevronUp, Star, Mail, User, Phone, Info, Zap, CreditCard } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import { WireframeGlobe } from '../components/WireframeGlobe';
// EmailJS configuration — sends reservation details to your email before PayPal redirect
const EMAILJS_SERVICE_ID = 'service_qvmqlhi';
const EMAILJS_TEMPLATE_ID = 'template_7ynesvi';
const EMAILJS_PUBLIC_KEY = '-083vwxCoxw7cbSCz';
export function CheckoutPage() {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'instant'
    });
  }, []);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMobileSummaryExpanded, setIsMobileSummaryExpanded] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = {
          ...prev
        };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  const isFormValid = formData.name.trim() !== '' && formData.email.trim() !== '' && /^\S+@\S+\.\S+$/.test(formData.email) && formData.phone.trim() !== '';
  const handlePay = async () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'El nombre es obligatorio';
    if (!formData.email.trim()) newErrors.email = 'El email es obligatorio';else if (!/^\S+@\S+\.\S+$/.test(formData.email)) newErrors.email = 'Email inválido';
    if (!formData.phone.trim()) newErrors.phone = 'El teléfono es obligatorio';
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    setIsSubmitting(true);
    // Send reservation details via EmailJS, then redirect to PayPal
    try {
      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message || 'Sin notas adicionales'
      }, EMAILJS_PUBLIC_KEY);
    } catch (error) {
      console.error('EmailJS error:', error);
      // Continue to PayPal even if email fails — user shouldn't lose their reservation flow
    }
    window.location.href = `https://paypal.me/Qualer100/50`;
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 10
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  const includedItems = ['Inicio inmediato del proyecto', 'Consulta estratégica personalizada', 'Diseño y desarrollo profesional', 'Soporte prioritario continuo'];
  return <div className="min-h-screen bg-[#060606] relative selection:bg-brand-emerald/20 selection:text-white font-sans antialiased" data-id="element-253">
      <div className="flex flex-col lg:flex-row min-h-screen" data-id="element-254">
        {/* ==================== LEFT PANEL ==================== */}
        <div className="lg:w-[45%] xl:w-[42%] relative bg-[#060606] lg:border-r border-white/[0.05]" data-id="element-255">
          <div className="max-w-[440px] ml-auto px-6 md:px-10 lg:pr-16 pt-8 lg:pt-12 pb-8 lg:pb-16 relative z-10" data-id="element-256">
            <motion.div variants={containerVariants} initial="hidden" animate="show" data-id="element-257">
              {/* Back — Desktop */}
              <motion.button variants={itemVariants} onClick={() => navigate(-1)} className="hidden lg:flex items-center gap-2 text-gray-500 hover:text-gray-300 text-[11px] uppercase tracking-[0.2em] font-medium mb-12 transition-colors" data-id="element-258">
                <ChevronLeft size={14} data-id="element-259" />
                Volver
              </motion.button>

              {/* Mobile Header */}
              <div className="flex lg:hidden items-center justify-between mb-8" data-id="element-260">
                <button onClick={() => navigate(-1)} className="flex items-center justify-center w-10 h-10 rounded-full bg-white/[0.03] border border-white/[0.06] text-gray-400 hover:text-white hover:bg-white/[0.06] transition-colors" data-id="element-261">
                  <ChevronLeft size={18} data-id="element-262" />
                </button>
                <div className="flex items-center gap-3" data-id="element-263">
                  <div className="w-8 h-8 rounded-lg bg-[#0a0a0a] border border-white/[0.08] flex items-center justify-center overflow-hidden" data-id="element-264">
                    <WireframeGlobe size={44} data-id="element-265" />
                  </div>
                  <span className="text-white font-semibold text-sm tracking-wide" data-id="element-266">
                    Growthly
                  </span>
                </div>
                <div className="w-10" data-id="element-267" />
              </div>

              {/* Brand — Desktop */}
              <motion.div variants={itemVariants} className="hidden lg:flex items-center gap-4 mb-12" data-id="element-268">
                <div className="w-12 h-12 rounded-xl bg-[#0a0a0a] border border-white/[0.1] flex items-center justify-center overflow-hidden" data-id="element-269">
                  <WireframeGlobe size={64} data-id="element-270" />
                </div>
                <div data-id="element-271">
                  <span className="text-white font-bold text-lg tracking-tight block mb-0.5" data-id="element-272">
                    Growthly
                  </span>
                  <span className="text-gray-500 text-[10px] uppercase tracking-[0.2em] font-medium" data-id="element-273">
                    Digital Ecosystem Agency
                  </span>
                </div>
              </motion.div>

              {/* Price */}
              <motion.div variants={itemVariants} className="mb-8" data-id="element-274">
                <p className="text-gray-500 text-[11px] font-semibold uppercase tracking-[0.2em] mb-3" data-id="element-275">
                  Reserva de Proyecto
                </p>
                <div className="flex items-baseline gap-1.5" data-id="element-276">
                  <span className="text-[56px] font-medium text-white leading-none tracking-tight tabular-nums" data-id="element-277">
                    50
                  </span>
                  <span className="text-[56px] font-medium text-white/30 leading-none tracking-tight tabular-nums" data-id="element-278">
                    ,00
                  </span>
                  <span className="text-gray-500 text-sm font-medium ml-1 self-end mb-2" data-id="element-279">
                    EUR
                  </span>
                </div>
              </motion.div>

              {/* Mobile Expand */}
              <div className="lg:hidden mb-8" data-id="element-280">
                <button onClick={() => setIsMobileSummaryExpanded(!isMobileSummaryExpanded)} className="flex items-center justify-between w-full py-3.5 px-5 rounded-xl bg-white/[0.02] border border-white/[0.05] text-sm text-gray-400 font-medium transition-colors hover:bg-white/[0.04]" data-id="element-281">
                  <span data-id="element-282">
                    {isMobileSummaryExpanded ? 'Ocultar detalles' : 'Ver detalles del pedido'}
                  </span>
                  {isMobileSummaryExpanded ? <ChevronUp size={16} className="text-gray-500" data-id="element-283" /> : <ChevronDown size={16} className="text-gray-500" data-id="element-284" />}
                </button>
              </div>

              {/* Expandable Details */}
              <div className={`${!isMobileSummaryExpanded ? 'hidden lg:block' : ''}`} data-id="element-285">
                {/* Deposit Explanation - NEW SECTION */}
                <motion.div variants={itemVariants} className="mb-10" data-id="element-286">
                  <div className="bg-brand-emerald/[0.03] border border-brand-emerald/20 rounded-2xl p-5" data-id="element-287">
                    <div className="flex items-center gap-2 mb-4" data-id="element-288">
                      <div className="w-6 h-6 rounded-full bg-brand-emerald/10 flex items-center justify-center" data-id="element-289">
                        <Info size={14} className="text-brand-emerald" data-id="element-290" />
                      </div>
                      <h3 className="text-white font-medium text-[14px]" data-id="element-291">
                        ¿Por qué una reserva de 50€?
                      </h3>
                    </div>

                    <div className="space-y-4" data-id="element-292">
                      <div className="flex gap-3" data-id="element-293">
                        <CreditCard size={16} className="text-gray-500 shrink-0 mt-0.5" data-id="element-294" />
                        <div data-id="element-295">
                          <p className="text-gray-300 text-[13px] font-medium mb-0.5" data-id="element-296">
                            Se descuenta del precio final
                          </p>
                          <p className="text-gray-500 text-[12px] leading-relaxed" data-id="element-297">
                            Los 50€ se restan íntegramente del presupuesto total
                            de tu proyecto. No es un coste adicional.
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-3" data-id="element-298">
                        <Zap size={16} className="text-gray-500 shrink-0 mt-0.5" data-id="element-299" />
                        <div data-id="element-300">
                          <p className="text-gray-300 text-[13px] font-medium mb-0.5" data-id="element-301">
                            Inicio inmediato
                          </p>
                          <p className="text-gray-500 text-[12px] leading-relaxed" data-id="element-302">
                            Tu reserva activa el inicio del proyecto de forma
                            prioritaria. Nos permite asignar recursos y comenzar
                            a trabajar para ti.
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-3" data-id="element-303">
                        <ShieldCheck size={16} className="text-gray-500 shrink-0 mt-0.5" data-id="element-304" />
                        <div data-id="element-305">
                          <p className="text-gray-300 text-[13px] font-medium mb-0.5" data-id="element-306">
                            Sin riesgo
                          </p>
                          <p className="text-gray-500 text-[12px] leading-relaxed" data-id="element-307">
                            Garantía de devolución del 100% si no estás
                            satisfecho con la propuesta inicial.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                <motion.div variants={itemVariants} className="h-px bg-white/[0.06] mb-8" data-id="element-308" />

                {/* Product card */}
                <motion.div variants={itemVariants} className="mb-8" data-id="element-309">
                  <div className="rounded-xl bg-white/[0.02] border border-white/[0.06] overflow-hidden" data-id="element-310">
                    <div className="p-5 flex items-center gap-4" data-id="element-311">
                      <div className="w-12 h-12 rounded-xl bg-[#0a0a0a] border border-white/[0.08] flex items-center justify-center shrink-0" data-id="element-312">
                        <WireframeGlobe size={48} data-id="element-313" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-314">
                        <h3 className="text-white font-medium text-[15px] mb-0.5" data-id="element-315">
                          Ecosistema Digital
                        </h3>
                        <p className="text-gray-500 text-[12px]" data-id="element-316">
                          Abono inicial · Proyecto completo
                        </p>
                      </div>
                      <div className="text-white font-medium text-[15px] tabular-nums" data-id="element-317">
                        50,00€
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Included */}
                <motion.div variants={itemVariants} className="mb-10" data-id="element-318">
                  <p className="text-[11px] text-gray-500 font-medium mb-4" data-id="element-319">
                    Incluido en tu reserva:
                  </p>
                  <div className="space-y-3" data-id="element-320">
                    {includedItems.map((item, i) => <div key={i} className="flex items-center gap-3" data-id="element-321">
                        <Check size={14} className="text-brand-emerald shrink-0" data-id="element-322" />
                        <span className="text-gray-400 text-[13px]" data-id="element-323">
                          {item}
                        </span>
                      </div>)}
                  </div>
                </motion.div>

                <motion.div variants={itemVariants} className="h-px bg-white/[0.06] mb-8" data-id="element-324" />

                {/* Totals */}
                <motion.div variants={itemVariants} className="space-y-3 mb-10" data-id="element-325">
                  <div className="flex justify-between text-[13px]" data-id="element-326">
                    <span className="text-gray-500" data-id="element-327">Subtotal</span>
                    <span className="text-gray-300 tabular-nums" data-id="element-328">50,00€</span>
                  </div>
                  <div className="flex justify-between text-[13px]" data-id="element-329">
                    <span className="text-gray-500" data-id="element-330">IVA</span>
                    <span className="text-gray-300" data-id="element-331">Incluido</span>
                  </div>
                  <div className="pt-3 mt-3 border-t border-white/[0.06] flex justify-between items-center" data-id="element-332">
                    <span className="text-white font-medium text-[15px]" data-id="element-333">
                      Total a pagar hoy
                    </span>
                    <div className="flex items-baseline gap-1" data-id="element-334">
                      <span className="text-white font-semibold text-xl tabular-nums" data-id="element-335">
                        50,00
                      </span>
                      <span className="text-gray-400 text-sm" data-id="element-336">€</span>
                    </div>
                  </div>
                </motion.div>

                {/* Social proof */}
                <motion.div variants={itemVariants} className="flex items-center gap-3 py-3 px-4 rounded-xl bg-white/[0.02] border border-white/[0.04]" data-id="element-337">
                  <div className="flex gap-0.5" data-id="element-338">
                    {[...Array(5)].map((_, i) => <Star key={i} size={12} className="text-brand-emerald fill-brand-emerald" data-id="element-339" />)}
                  </div>
                  <div className="text-gray-400 text-[12px]" data-id="element-340">
                    <span className="text-white font-medium" data-id="element-341">+4.000</span>{' '}
                    proyectos completados
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* ==================== RIGHT PANEL ==================== */}
        <div className="w-full lg:w-[55%] xl:w-[58%] bg-[#0a0a0a] min-h-screen relative" data-id="element-342">
          <div className="max-w-[560px] mx-auto lg:mx-0 lg:mr-auto px-6 md:px-10 lg:pl-16 pt-12 lg:pt-20 pb-36 lg:pb-24 relative z-10" data-id="element-343">
            <motion.div variants={containerVariants} initial="hidden" animate="show" data-id="element-344">
              {/* Form Container */}
              <div className="bg-white/[0.02] border border-white/[0.05] rounded-3xl p-6 md:p-10" data-id="element-345">
                {/* Section 01 */}
                <motion.div variants={itemVariants} className="mb-8 flex items-baseline gap-4" data-id="element-346">
                  <span className="text-[32px] font-light text-brand-emerald/40 leading-none" data-id="element-347">
                    01
                  </span>
                  <div data-id="element-348">
                    <h2 className="text-white font-medium text-lg" data-id="element-349">Contacto</h2>
                    <p className="text-gray-500 text-[13px] mt-1" data-id="element-350">
                      Para enviarte la confirmación del proyecto
                    </p>
                  </div>
                </motion.div>

                {/* Email */}
                <motion.div variants={itemVariants} className="mb-8" data-id="element-351">
                  <label className="block text-[11px] text-gray-400 uppercase tracking-[0.1em] font-medium mb-2 ml-1" data-id="element-352">
                    Correo electrónico
                  </label>
                  <div className="relative" data-id="element-353">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-354">
                      <Mail size={16} className={`transition-colors duration-300 ${focusedField === 'email' ? 'text-brand-emerald' : 'text-gray-600'}`} data-id="element-355" />
                    </div>
                    <input type="email" name="email" value={formData.email} onChange={handleInputChange} onFocus={() => setFocusedField('email')} onBlur={() => setFocusedField(null)} className={`w-full bg-[#060606] border ${errors.email ? 'border-red-500/40' : 'border-white/[0.08]'} rounded-xl pl-11 pr-4 py-3.5 text-white text-[15px] focus:outline-none focus:border-brand-emerald/40 focus:bg-[#0a0a0a] transition-all duration-300 placeholder:text-gray-700`} placeholder="tu@correo.com" data-id="element-356" />
                  </div>
                  {errors.email && <p className="text-red-400/90 text-[12px] mt-2 ml-1" data-id="element-357">
                      {errors.email}
                    </p>}
                </motion.div>

                <motion.div variants={itemVariants} className="h-px bg-white/[0.06] my-8" data-id="element-358" />

                {/* Section 02 */}
                <motion.div variants={itemVariants} className="mb-8 flex items-baseline gap-4" data-id="element-359">
                  <span className="text-[32px] font-light text-brand-emerald/40 leading-none" data-id="element-360">
                    02
                  </span>
                  <div data-id="element-361">
                    <h2 className="text-white font-medium text-lg" data-id="element-362">
                      Datos personales
                    </h2>
                  </div>
                </motion.div>

                {/* Name + Phone */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6" data-id="element-363">
                  <motion.div variants={itemVariants} data-id="element-364">
                    <label className="block text-[11px] text-gray-400 uppercase tracking-[0.1em] font-medium mb-2 ml-1" data-id="element-365">
                      Nombre completo
                    </label>
                    <div className="relative" data-id="element-366">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-367">
                        <User size={16} className={`transition-colors duration-300 ${focusedField === 'name' ? 'text-brand-emerald' : 'text-gray-600'}`} data-id="element-368" />
                      </div>
                      <input type="text" name="name" value={formData.name} onChange={handleInputChange} onFocus={() => setFocusedField('name')} onBlur={() => setFocusedField(null)} className={`w-full bg-[#060606] border ${errors.name ? 'border-red-500/40' : 'border-white/[0.08]'} rounded-xl pl-11 pr-4 py-3.5 text-white text-[15px] focus:outline-none focus:border-brand-emerald/40 focus:bg-[#0a0a0a] transition-all duration-300 placeholder:text-gray-700`} placeholder="Juan Pérez" data-id="element-369" />
                    </div>
                    {errors.name && <p className="text-red-400/90 text-[12px] mt-2 ml-1" data-id="element-370">
                        {errors.name}
                      </p>}
                  </motion.div>

                  <motion.div variants={itemVariants} data-id="element-371">
                    <label className="block text-[11px] text-gray-400 uppercase tracking-[0.1em] font-medium mb-2 ml-1" data-id="element-372">
                      Teléfono
                    </label>
                    <div className="relative" data-id="element-373">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-374">
                        <Phone size={16} className={`transition-colors duration-300 ${focusedField === 'phone' ? 'text-brand-emerald' : 'text-gray-600'}`} data-id="element-375" />
                      </div>
                      <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} onFocus={() => setFocusedField('phone')} onBlur={() => setFocusedField(null)} className={`w-full bg-[#060606] border ${errors.phone ? 'border-red-500/40' : 'border-white/[0.08]'} rounded-xl pl-11 pr-4 py-3.5 text-white text-[15px] focus:outline-none focus:border-brand-emerald/40 focus:bg-[#0a0a0a] transition-all duration-300 placeholder:text-gray-700`} placeholder="+34 600 000 000" data-id="element-376" />
                    </div>
                    {errors.phone && <p className="text-red-400/90 text-[12px] mt-2 ml-1" data-id="element-377">
                        {errors.phone}
                      </p>}
                  </motion.div>
                </div>

                {/* Message */}
                <motion.div variants={itemVariants} className="mb-8" data-id="element-378">
                  <label className="block text-[11px] text-gray-400 uppercase tracking-[0.1em] font-medium mb-2 ml-1" data-id="element-379">
                    Notas{' '}
                    <span className="text-gray-600 normal-case tracking-normal" data-id="element-380">
                      (opcional)
                    </span>
                  </label>
                  <textarea name="message" value={formData.message} onChange={handleInputChange} onFocus={() => setFocusedField('message')} onBlur={() => setFocusedField(null)} rows={3} className="w-full bg-[#060606] border border-white/[0.08] rounded-xl px-4 py-3.5 text-white text-[15px] focus:outline-none focus:border-brand-emerald/40 focus:bg-[#0a0a0a] transition-all duration-300 resize-none placeholder:text-gray-700" placeholder="Detalles sobre tu proyecto..." data-id="element-381" />
                </motion.div>

                {/* Pay Button */}
                <motion.div variants={itemVariants} data-id="element-382">
                  <button onClick={handlePay} disabled={isSubmitting} className={`w-full flex justify-center items-center gap-2.5 px-6 py-[18px] rounded-xl font-medium text-[15px] transition-all duration-300 ${isSubmitting ? 'bg-brand-emerald/30 text-white/50 cursor-wait' : 'bg-brand-emerald text-[#060606] hover:bg-brand-emeraldLight hover:shadow-[0_0_20px_rgba(16,185,129,0.3)]'}`} data-id="element-383">
                    {isSubmitting ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-384" /> : <>
                        <Lock size={16} data-id="element-385" />
                        <span data-id="element-386">Completar reserva · 50,00€</span>
                      </>}
                  </button>
                </motion.div>

                {/* PayPal note */}
                <motion.div variants={itemVariants} className="flex items-center justify-center gap-2 mt-5" data-id="element-387">
                  <Lock size={12} className="text-gray-600" data-id="element-388" />
                  <p className="text-gray-500 text-[12px]" data-id="element-389">
                    Serás redirigido a PayPal · Pago seguro
                  </p>
                </motion.div>
              </div>

              {/* Trust row */}
              <motion.div variants={itemVariants} className="mt-8 flex items-center justify-center gap-6 text-[12px] text-gray-500" data-id="element-390">
                <div className="flex items-center gap-1.5" data-id="element-391">
                  <ShieldCheck size={14} className="text-gray-400" data-id="element-392" />
                  <span data-id="element-393">Encriptado</span>
                </div>
                <div className="flex items-center gap-1.5" data-id="element-394">
                  <Check size={14} className="text-gray-400" data-id="element-395" />
                  <span data-id="element-396">Confirmación inmediata</span>
                </div>
              </motion.div>

              {/* Direct contact - builds trust before payment */}
              <motion.div variants={itemVariants} className="mt-6 p-5 rounded-2xl bg-white/[0.02] border border-white/[0.05]" data-id="element-397">
                <p className="text-center text-gray-400 text-[13px] mb-4" data-id="element-398">
                  ¿Tienes dudas antes de reservar? Estamos disponibles.
                </p>
                <div className="grid grid-cols-2 gap-3" data-id="element-399">
                  <a href="tel:+34631012592" className="flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.06] hover:border-brand-emerald/30 transition-all duration-300 group" data-id="element-400">
                    <Phone size={14} className="text-brand-emerald" data-id="element-401" />
                    <span className="text-white text-[13px] font-medium group-hover:text-brand-emerald transition-colors" data-id="element-402">
                      Llamar
                    </span>
                  </a>
                  <a href="https://wa.me/34631012592" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.06] hover:border-[#25D366]/30 transition-all duration-300 group" data-id="element-403">
                    <svg className="w-3.5 h-3.5 text-[#25D366]" fill="currentColor" viewBox="0 0 24 24" data-id="element-404">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" data-id="element-405" />
                    </svg>
                    <span className="text-white text-[13px] font-medium group-hover:text-[#25D366] transition-colors" data-id="element-406">
                      WhatsApp
                    </span>
                  </a>
                </div>
                <p className="text-center text-gray-600 text-[11px] mt-3 font-medium" data-id="element-407">
                  +34 631 01 25 92
                </p>
              </motion.div>
            </motion.div>
          </div>

          {/* Mobile sticky button */}
          <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50" data-id="element-408">
            <div className="h-6 bg-gradient-to-t from-[#0a0a0a] to-transparent pointer-events-none" data-id="element-409" />
            <div className="p-4 bg-[#0a0a0a] border-t border-white/[0.06] pb-8" data-id="element-410">
              <button onClick={handlePay} disabled={isSubmitting} className={`w-full flex justify-center items-center gap-2.5 px-6 py-[18px] rounded-xl font-medium text-[15px] transition-all duration-300 ${isSubmitting ? 'bg-brand-emerald/30 text-white/50 cursor-wait' : 'bg-brand-emerald text-[#060606]'}`} data-id="element-411">
                {isSubmitting ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-412" /> : <>
                    <Lock size={16} data-id="element-413" />
                    <span data-id="element-414">Completar reserva · 50,00€</span>
                  </>}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}